import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class GoogleSheetLink extends ModelObject<GoogleSheetLink> {

    /**
    * User to which this configuration belongs to.
    */
    public userId: Guid = Guid.createEmpty();
    /**
    * A token to authorise access to the current packages resources
    * Not Nullable
    * Max Length : 10000
    */
    public accessToken: string = "";
    private _accessTokenExpiresAt: Instant | null = null;
    /**
    * The timestamp when the access token will expire and need refreshing/updating 
    */
    public get accessTokenExpiresAt(): Instant | null { return this._accessTokenExpiresAt }
    public set accessTokenExpiresAt(value: Instant | null) {
      this._accessTokenExpiresAt = value === undefined ? null : value;
    }
    /**
    * A token to refresh access tokens
    * Not Nullable
    * Max Length : 10000
    */
    public refreshToken: string = "";
    /**
    * A unique generated code identifying the current in progress oauth flow, used for preventing xsrf.
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public nonce: string = "";
    /**
    * The callback url used for the current link establishment attempt.
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public oauthCallbackUrl: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.accessToken = jsonSerializedObject.accessToken;
          this.accessTokenExpiresAt = jsonSerializedObject.accessTokenExpiresAt == null ? null : Instant.parse(jsonSerializedObject.accessTokenExpiresAt);
          this.refreshToken = jsonSerializedObject.refreshToken;
          this.nonce = jsonSerializedObject.nonce;
          this.oauthCallbackUrl = jsonSerializedObject.oauthCallbackUrl;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.userId = this.userId.toString();
        toRet.accessToken = this.accessToken;
        toRet.accessTokenExpiresAt = this.accessTokenExpiresAt == null ? null : this.accessTokenExpiresAt.toString();
        toRet.refreshToken = this.refreshToken;
        toRet.nonce = this.nonce;
        toRet.oauthCallbackUrl = this.oauthCallbackUrl;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "userId", rule: (v: any) => !v.isEmpty() || "GoogleSheetLink.UserId cannot be empty"},
            {field: "accessToken", rule: (v: any) => v == null || v.length <= 10000 || "AccessToken has too many characters (10000)"},
            {field: "refreshToken", rule: (v: any) => v == null || v.length <= 10000 || "RefreshToken has too many characters (10000)"},
    ];
}


export class GoogleSheetLinkService extends ServiceBase {

  public async isGoogleSheetLinkActive(tenantId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:tenantId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => response.active;;
    return this.makeJsonRequest("googleSheetLink/v1/isGoogleSheetLinkActive", req, false, extractor, opts);
  };

  public async commenceGoogleSheetLink(tenantId: Guid, userId: Guid, callbackUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:tenantId.toString(),
    userId:userId.toString(),
    callbackUrl:callbackUrl,
    };
    const extractor = (response:any) => response.redirectUrl;;
    return this.makeJsonRequest("googleSheetLink/v1/commenceGoogleSheetLink", req, true, extractor, opts);
  };

  public async completeGoogleSheetLink(tenantId: Guid, state: string, code: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    state:state,
    code:code,
    };
    const extractor = null;
    return this.makeJsonRequest("googleSheetLink/v1/completeGoogleSheetLink", req, true, extractor, opts);
  };
}

export const googleSheetLinkService = new GoogleSheetLinkService();